/**
 * Chart series palette — drawn from the app's brand resource-type hues (denim,
 * sage, mauve, ochre, spruce, rose, pine, brown) at chart-strength chroma so
 * adjacent bars/series read as distinct instead of a single warm blur.
 *
 * The order is not arbitrary: it interleaves cool and warm hues so no two
 * red/green-confusable colors sit next to each other, keeping the sequence
 * colorblind-safe (validated against the cream chart surface — passes the
 * lightness-band, chroma-floor, CVD-separation, and contrast checks).
 *
 * These are the SSR / no-CSS fallback. At runtime `getChartPaletteColor` prefers
 * the `--chart-series-N` CSS custom properties (see `app/v1/v1.css`), which are
 * kept in sync with this array. Note this is a *chart-only* token set, distinct
 * from the shared `--chart-N` tokens (used by buttons/toasts/pills/calendars).
 */
export const CHART_SERIES_COLORS = [
  'oklch(0.58 0.13 255)' /* denim (blue) */,
  'oklch(0.61 0.12 150)' /* sage (green) */,
  'oklch(0.56 0.13 320)' /* mauve (violet) */,
  'oklch(0.62 0.13 70)' /* ochre (amber) */,
  'oklch(0.55 0.12 222)' /* spruce (steel blue) */,
  'oklch(0.61 0.14 15)' /* rose (red) */,
  'oklch(0.60 0.115 190)' /* pine (teal) */,
  'oklch(0.52 0.12 45)' /* brown (rust) */,
] as const;

export const CHART_PALETTE_SIZE = CHART_SERIES_COLORS.length;

function getChartThemeRoot(): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const v1Shell = document.querySelector('.v1-app-shell');
  if (v1Shell instanceof HTMLElement) {
    return v1Shell;
  }
  return document.documentElement;
}

function readChartThemeVar(name: string, fallback: string): string {
  const root = getChartThemeRoot();
  if (root === null) {
    return fallback;
  }
  const value = getComputedStyle(root).getPropertyValue(name).trim();
  return value !== '' ? value : fallback;
}

// Lightness offset applied per palette "ring" so charts with more series than
// CHART_PALETTE_SIZE still get distinct colors from the same 8 hues instead of
// repeating: ring 0 = as-is, ring 1 = darker, ring 2 = lighter. Past these
// (>24 series) colors repeat — acceptable since that many series is rare and
// unreadable regardless.
const CHART_RING_LIGHTNESS_DELTAS = [0, -0.16, 0.15] as const;

/** Shift an `oklch(L C H)` string's lightness, clamped to a visible band. */
function shiftOklchLightness(color: string, deltaL: number): string {
  if (deltaL === 0) {
    return color;
  }
  const match = color.match(/^oklch\(\s*([\d.]+%?)\s+(\S+)\s+([^\s/)]+)/i);
  if (match === null) {
    return color;
  }
  const [, rawL, rawC, rawH] = match;
  if (rawL === undefined || rawC === undefined || rawH === undefined) {
    return color;
  }
  const baseL = rawL.endsWith('%')
    ? Number.parseFloat(rawL) / 100
    : Number.parseFloat(rawL);
  if (Number.isNaN(baseL)) {
    return color;
  }
  const nextL = Math.min(0.82, Math.max(0.4, baseL + deltaL));
  return `oklch(${Number(nextL.toFixed(3))} ${rawC} ${rawH})`;
}

export function getChartPaletteColor(index: number): string {
  const safeIndex = index < 0 ? 0 : index;
  const slot = (safeIndex % CHART_PALETTE_SIZE) + 1;
  const fallback =
    CHART_SERIES_COLORS[safeIndex % CHART_PALETTE_SIZE] ??
    CHART_SERIES_COLORS[0];
  const base = readChartThemeVar(`--chart-series-${slot}`, fallback);
  const ring = Math.floor(safeIndex / CHART_PALETTE_SIZE);
  const delta =
    CHART_RING_LIGHTNESS_DELTAS[ring % CHART_RING_LIGHTNESS_DELTAS.length] ?? 0;
  return shiftOklchLightness(base, delta);
}

export function readChartThemeColor(name: string, fallback: string): string {
  return readChartThemeVar(name, fallback);
}
